import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { cn } from "~/lib/cn";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  getSummitCtaDataOptions,
  updateSummitCta,
} from "~/lib/functions/summit";

/**
 * Admin page for controlling summit CTA button visibility.
 * Allows staff to show/hide the CTA instantly during live streams.
 *
 * Search: summit_cta_admin, call_to_action_control
 */

const AdvancedFormSchema = z.object({
  ctaText: z.string(),
  ctaUrl: z
    .string()
    .url({ message: "Please enter a valid URL (e.g., https://example.com)" })
    .or(z.literal("")),
  ctaAppearsOn: z.string(),
});

type AdvancedFormData = z.infer<typeof AdvancedFormSchema>;

export const Route = createFileRoute("/admin/summit-cta/")({
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "summit:read"),
  loader: async ({ context }) => {
    const ctaData = await context.queryClient.ensureQueryData(
      getSummitCtaDataOptions,
    );
    return { ctaData };
  },
  component: SummitCtaAdminPage,
});

// All times are displayed and entered in US Eastern Time (EST/EDT)
const EST_TIMEZONE = "America/New_York";

const dateTimeFormatterEST = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZone: EST_TIMEZONE,
});

/**
 * Format a UTC date for display in EST timezone
 */
function formatDateTimeForDisplayEST(date: Date | string | null): string {
  if (!date) return "";
  // Ensure we have a proper Date object
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "Invalid date";
  return `${dateTimeFormatterEST.format(d)} ET`;
}

/**
 * Format a UTC date as YYYY-MM-DDTHH:mm in EST timezone for datetime-local input
 */
function formatDateTimeForInputEST(date: Date | string | null): string {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  // Get the date/time parts in EST timezone
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: EST_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const get = (type: string): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

/**
 * Parse a datetime-local input value (interpreted as EST) to a UTC Date
 */
function parseInputAsEST(value: string): Date | null {
  if (!value) return null;
  // The input value is in format YYYY-MM-DDTHH:mm
  // We need to interpret this as EST and convert to UTC
  const [datePart, timePart] = value.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);

  // Create a temp date to determine if we're in EST or EDT
  const tempDate = new Date(year, month - 1, day, hours, minutes);

  // Get the UTC offset for EST at this date/time
  const estFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: EST_TIMEZONE,
    timeZoneName: "shortOffset",
  });
  const offsetMatch = estFormatter
    .formatToParts(tempDate)
    .find((p) => p.type === "timeZoneName");
  const offsetStr = offsetMatch?.value ?? "GMT-5"; // Default to EST

  // Build ISO string with timezone offset
  // offsetStr is like "GMT-5" or "GMT-4", we need "-05:00" or "-04:00"
  const offsetNum = Number.parseInt(offsetStr.replace("GMT", ""), 10);
  const offsetFormatted = `${offsetNum < 0 ? "-" : "+"}${String(Math.abs(offsetNum)).padStart(2, "0")}:00`;

  const isoString = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00${offsetFormatted}`;

  return new Date(isoString);
}

function SummitCtaAdminPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { data: ctaData, isLoading } = useQuery({
    ...getSummitCtaDataOptions,
    refetchInterval: 5000, // More frequent polling for admin view
  });

  const form = useForm<AdvancedFormData>({
    resolver: zodResolver(AdvancedFormSchema),
    values: {
      ctaText: ctaData?.ctaText ?? "",
      ctaUrl: ctaData?.ctaUrl ?? "",
      ctaAppearsOn: formatDateTimeForInputEST(ctaData?.ctaAppearsOn ?? null),
    },
  });

  const mutation = useMutation({
    mutationFn: updateSummitCta,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["summit", "cta"] });
      queryClient.invalidateQueries({ queryKey: ["summit", "active"] });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const handleShowCta = (): void => {
    if (!ctaData?.summitId) return;
    mutation.mutate({
      data: {
        summitId: ctaData.summitId,
        ctaAppearsOn: new Date(),
      },
    });
  };

  const handleHideCta = (): void => {
    if (!ctaData?.summitId) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    mutation.mutate({
      data: {
        summitId: ctaData.summitId,
        ctaAppearsOn: tomorrow,
      },
    });
  };

  const handleSaveAdvanced = form.handleSubmit((data) => {
    if (!ctaData?.summitId) return;
    mutation.mutate({
      data: {
        summitId: ctaData.summitId,
        ctaText: data.ctaText || null,
        ctaUrl: data.ctaUrl || null,
        ctaAppearsOn: parseInputAsEST(data.ctaAppearsOn),
      },
    });
  });

  // Determine visibility status
  const isCtaConfigured = ctaData?.ctaText && ctaData?.ctaUrl;
  const isCtaVisible =
    isCtaConfigured &&
    ctaData?.ctaAppearsOn &&
    new Date() >= new Date(ctaData.ctaAppearsOn);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!ctaData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">No active summit found.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-2xl">
        <h1 className="mb-2 text-2xl font-bold text-gray-900">
          Summit CTA Control
        </h1>

        {/* Timezone Notice */}
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <strong>All times on this page are in US Eastern Time (ET)</strong>
        </div>

        {/* Status Banner */}
        {saveSuccess && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            Changes saved successfully!
          </div>
        )}

        {mutation.error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Error saving changes. Please try again.
          </div>
        )}

        {/* CTA Preview */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold tracking-wide text-gray-500 uppercase">
            Current CTA Preview
          </h2>

          {isCtaConfigured ? (
            <div className="space-y-4">
              {/* Button Preview */}
              <div className="flex justify-center rounded-lg border border-gray-200 bg-gray-50 p-6">
                <button
                  type="button"
                  className="rounded-lg bg-brand-600 px-6 py-3 font-semibold text-white shadow-md hover:bg-brand-700"
                  onClick={() =>
                    ctaData.ctaUrl && window.open(ctaData.ctaUrl, "_blank")
                  }
                >
                  {ctaData.ctaText}
                </button>
              </div>

              {/* Details */}
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="font-medium text-gray-500">URL:</span>
                  <a
                    href={ctaData.ctaUrl ?? "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-brand-600 hover:underline"
                  >
                    {ctaData.ctaUrl}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-500">Appears at:</span>
                  <span className="text-gray-700">
                    {ctaData.ctaAppearsOn
                      ? formatDateTimeForDisplayEST(ctaData.ctaAppearsOn)
                      : "Not scheduled"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-500">Status:</span>
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium",
                      isCtaVisible
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-gray-100 text-gray-600",
                    )}
                  >
                    {isCtaVisible ? (
                      <>
                        <Eye className="h-3 w-3" />
                        Visible to viewers
                      </>
                    ) : (
                      <>
                        <EyeOff className="h-3 w-3" />
                        Hidden
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
              CTA is not configured. Please set the text and URL in the advanced
              settings below.
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold tracking-wide text-gray-500 uppercase">
            Quick Actions
          </h2>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleShowCta}
              disabled={mutation.isPending || !isCtaConfigured}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg px-5 py-3 font-semibold shadow-sm transition",
                isCtaConfigured
                  ? "bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  : "cursor-not-allowed bg-gray-200 text-gray-400",
              )}
            >
              <Eye className="h-4 w-4" />
              Show CTA Now
            </button>

            <button
              type="button"
              onClick={handleHideCta}
              disabled={mutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-600 px-5 py-3 font-semibold text-white shadow-sm transition hover:bg-gray-700 disabled:opacity-50"
            >
              <EyeOff className="h-4 w-4" />
              Hide CTA
            </button>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            "Show CTA Now" sets the visibility time to right now. "Hide CTA"
            sets it to tomorrow.
          </p>
        </div>

        {/* Advanced Settings (Collapsible) */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <button
            type="button"
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            className="flex w-full items-center justify-between px-6 py-4 text-left"
          >
            <h2 className="text-sm font-semibold tracking-wide text-gray-500 uppercase">
              Advanced Settings
            </h2>
            {isAdvancedOpen ? (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronRight className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {isAdvancedOpen && (
            <form onSubmit={handleSaveAdvanced} className="border-t px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="ctaText"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Button Text
                  </label>
                  <input
                    id="ctaText"
                    type="text"
                    {...form.register("ctaText")}
                    placeholder="e.g., Get the Offer"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  />
                </div>

                <div>
                  <label
                    htmlFor="ctaUrl"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Button URL
                  </label>
                  <input
                    id="ctaUrl"
                    type="url"
                    {...form.register("ctaUrl")}
                    placeholder="https://..."
                    className={cn(
                      "w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:ring-2 focus:outline-none",
                      form.formState.errors.ctaUrl
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-200 focus:border-brand-500 focus:ring-brand-100",
                    )}
                  />
                  {form.formState.errors.ctaUrl && (
                    <p className="mt-1 text-sm text-red-600">
                      {form.formState.errors.ctaUrl.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="ctaAppearsOn"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Visibility Time{" "}
                    <span className="font-bold text-amber-600">
                      (Eastern Time)
                    </span>
                  </label>
                  <input
                    id="ctaAppearsOn"
                    type="datetime-local"
                    {...form.register("ctaAppearsOn")}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the time in <strong>US Eastern Time (ET)</strong>. The
                    CTA will be visible to viewers when this time is reached.
                  </p>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={mutation.isPending}
                    className="rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
                  >
                    {mutation.isPending ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Info */}
        <p className="mt-6 text-center text-xs text-gray-400">
          Changes take effect within 30 seconds on viewer screens.
        </p>
      </div>
    </div>
  );
}
